import { useAuth0 } from '@auth0/auth0-react';
import React, { useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useAppDispatch } from '~/hooks/useAppDispatch';
import { FlowTypes, ApplicationAuthType } from '~/enums';
import { onboardingLoginMethods } from '~/helpers/authentication.helper';
import useModals from '~/hooks/useModals';
import useMarketConfig from '~/hooks/useMarketConfig';
import useTranslate from '~/hooks/useTranslate';
import AuthPopup from '../AuthPopup/AuthPopup';
import { pushToGtmLoanAction } from '~/store/actions/gtmActions';
import { patchApplication } from '~/store/actions/loanApplicationActions';
import { updateForm } from '~/store/slices/loanApplication.slice';
import { RootState } from '~/store';

const useSubmit = () => {
  const t = useTranslate();
  const dispatch = useAppDispatch();
  const modals = useModals();

  const { loginWithRedirect } = useAuth0();

  const { marketRequiresLoginBeforeSubmittingApplication } = useMarketConfig();

  const {
    loanApplication: { flowType },
    intl: { market },
  } = useSelector((state: RootState) => state);

  const onAuthMethodSelected = useCallback(
    async (connectionName: string) => {
      const url = new URL(window.location.href);
      url.searchParams.set('authType', ApplicationAuthType.ManualSubmit);
      await dispatch(patchApplication({ currentStepCompleted: false }));

      loginWithRedirect({
        authorizationParams: {
          connection: connectionName,
          redirectUri: url.toString(),
        },
      });
    },
    [dispatch, flowType, loginWithRedirect, market]
  );

  const openAuthPopup = () => {
    modals.openModal({
      id: 'authPopup-modal',
      header: t('Onboarding.AuthPopupTitle') as string,
      children: (
        <AuthPopup
          loginMethods={onboardingLoginMethods[market]}
          onAuthMethodSelected={onAuthMethodSelected}
          bottomCTA={{
            text: t('Cancel') as string,
            action: () => {
              modals.closeModal('authPopup-modal');
            },
          }}
        />
      ),
      size: 'xs',
      heightAuto: true,
      hideCloseButton: true,
    });
  };

  const onSubmit = useCallback(async () => {
    dispatch(pushToGtmLoanAction({ actionName: 'submit_button_pressed' }));

    if (
      !marketRequiresLoginBeforeSubmittingApplication &&
      flowType === FlowTypes.Manual
    ) {
      dispatch(updateForm({ applicantIdentified: true }));
    }

    if (
      flowType === FlowTypes.Manual &&
      marketRequiresLoginBeforeSubmittingApplication
    ) {
      if (onboardingLoginMethods[market].length > 1) {
        openAuthPopup();
      } else {
        onAuthMethodSelected(onboardingLoginMethods[market][0].connectionName);
      }
    } else {
      dispatch(patchApplication({ finalized: true }));
    }
  }, [dispatch, flowType, loginWithRedirect, market]);

  return onSubmit;
};

export default useSubmit;
